const utils = require('./util');

const {
  getStyleString,
  returnStringIfExists
} = utils;


const SESEmailBuilderFactory = ({
  appName,
  endpoint,
  defaultEmailSource,
  defaultHeaderImage,
  defaultHeaderTextStyle = {},
  defaultHeaderStyle,
  defaultSubHeaderStyle = {},
  defaultSubHeaderTextStyle = {},
  defaultFont = 'arial',
  defaultFontColor = '#222222',
  defaultHeaderImageStyle,
  defaultBodyStyle = {},
  defaultContentStyle = {},
  defaultLabelStyle = {},
  defaultValueStyle = {},
  defaultLinkStyle = {
    textDecoration: 'none'
  },
  defaultFooterStyle = {},
  defaultFooterText = '',
  defaultFooterTextStyle = {},
  defaultButtonStyle = {},
}) => {
  class SESEmailBuilder {
    constructor() {
      this.data = {};
      this.onlySetIfNotExists = false;
      this.header = '';
      this.subheader = ''
      this.body = ''
      this.footer = ''
      this.bodyStyle = defaultBodyStyle
      this.defaultFont = defaultFont;
      this.defaultFontColor = defaultFontColor;
      this.contentStyle = defaultContentStyle;
      this.textStyle = {
        p: {},
        h1: {},
        h2: {},
        h3: {},
        h4: {},
        h5: {},
      };
    }

    setKeyTo(key, setTo) {
      if (!setTo) return;
      const split = key.split('.');
      let currentRef = this.data;
      for (let i = 0; i < split.length; i += 1) {
        const key = split[i];
        if (i === split.length - 1) {
          if (this.onlySetIfNotExists && currentRef[key] !== undefined) return;
          currentRef[key] = setTo;
          return;
        }
        if (!currentRef[key]) {
          currentRef[key] = {};
        }
        currentRef = currentRef[key];
      }
    }

    setDefaultFont(font) {
      this.defaultFont = font;
      return this;
    }

    setDefaultFontColor(color) {
      this.defaultFontColor = color;
      return this;
    }

    setContentStyle(style) {
      this.contentStyle = style;
      return this;
    }

    setSource(source) {
      this.setKeyTo('Source', source);
      return this;
    }

    setTo(to) {
      if (typeof to === 'string') {
        to = [to]
      }
      this.setKeyTo('Destination.ToAddresses', to);
      return this;
    }

    setBcc(to) {
      if (typeof to === 'string') {
        to = [to]
      }
      this.setKeyTo('Destination.BccAddresses', to);
      return this;
    }

    setCc(to) {
      if (typeof to === 'string') {
        to = [to]
      }
      this.setKeyTo('Destination.CcAddresses', to);
      return this;
    }

    setSubject(subject) {
      this.setKeyTo('Message.Subject.Data', subject);
      return this;
    }

    setTextStyle(tags, styleObj) {
      if (typeof tags === 'string') {
        tags = [tags];
      }

      tags.forEach((tag) => {
        this.textStyle[tag] = styleObj;
      });
      return this;
    }

    getTextTag(tag, text, textStyle = {}) {
      const style = { ...this.textStyle[tag], ...textStyle };
      if (!style.color) {
        style.color = this.defaultFontColor;
      }

      if (!style.fontFamily) {
        style.fontFamily = this.defaultFont
      }

      const styleString = getStyleString(style);
      return returnStringIfExists(`<${tag} style='${styleString}'>${text}</${tag}>`, text)
    }

    setHeader({
      text = appName,
      imageSource = defaultHeaderImage,
      style = defaultHeaderStyle,
      imgStyle = defaultHeaderImageStyle,
      textStyle = defaultHeaderTextStyle
    }) {
      this.header = `
        <div class='header' style='${getStyleString({ display: 'flex', alignItems: 'center', ...style })}'>
          ${returnStringIfExists(`
            <img src='${imageSource}' style='${getStyleString({ maxHeight: '100%', height: '100%', marginRight: '30px', ...imgStyle})}' >
          `, imageSource)}

          ${this.getTextTag('h3', text, textStyle)}
        </div>
      `.trim();
      return this;
    }

    setSubheader({ 
      text,
      style = defaultSubHeaderStyle,
      textStyle = defaultSubHeaderTextStyle
    }) {
      this.subheader = `
        <div class='subheader' style='${getStyleString(style)}'>
          ${this.getTextTag('p', text, textStyle)}
        </div>
      `.trim();
      return this;
    }

    addText({
      tag = 'p',
      text,
      textStyle
    }) {
      this.body += `
        ${this.getTextTag(tag, text, textStyle)}
      `.trim()
      return this;
    }


    addCustom(html = '') {
      this.body += html;
      return this;
    }

    addLineBreak() {
      this.body += '<br/>'
      return this;
    }

    addInfoItem({ 
      label,
      value,
      labelTag = 'h3',
      valueTag = 'p',
      labelStyle = defaultLabelStyle,
      valueStyle = defaultValueStyle
    }) {
      this.body += `
        ${this.getTextTag(labelTag, label, {
          margin: '10px 0',
          ...labelStyle
        })}
        ${this.getTextTag(valueTag, value, {
          marginLeft: '10px',
          opacity: 0.8,
          ...valueStyle
        })}
      `.trim()
      return this;
    }

    addButtons({
      buttons,
      wrapperStyle = {}
    }) {

      if (!Array.isArray(buttons)) {
        buttons = [buttons];
      }

      this.body += `
        <div style='${getStyleString({ display: 'flex', ...wrapperStyle })}'>
          ${
            buttons.reduce((acc, button, index) => {
              const {
                text,
                to,
                style = defaultButtonStyle
              } = button;

              return acc + `
                <a href='${to}'>
                  <button style='${getStyleString({
                    marginLeft: index === 0 ? 0 : '10px',
                    ...style
                  })}'>
                    ${text}
                  </button>
                </a>
              `.trim()
            }, '')
          }
        </div>
      `.trim()
      return this;
    }

    addJSON({
      data,
      labelTag = 'h3',
      valueTag = 'p',
      labelStyle = defaultLabelStyle,
      valueStyle = defaultValueStyle
    }) {
      const keys = Object.keys(data);
      keys.forEach(label => {
        this.addInfoItem({
          label: `${label}:`,
          value: `${data[label]}`,
          labelTag,
          valueTag,
          labelStyle,
          valueStyle
        })
      })
      return this;
    }

    setBodyStyle(style) {
      this.bodyStyle = style;
      return this;
    }

    setFooter({
      text = defaultFooterText,
      style = defaultFooterStyle,
      textStyle = defaultFooterTextStyle
    }) {
      this.footer = `
        <div class='footer' style='${getStyleString(style)}'>
          ${this.getTextTag('p', text, textStyle)}
        </div>
      `.trim();
      return this;
    }

    static link({
      text,
      to,
      style = defaultLinkStyle
    }) {
      return `<a style='${getStyleString({color: defaultFontColor,...style})}' href='${to}'>${text}</a>`
    }

    getHTML() {
      this.onlySetIfNotExists = true;
      

      if (!this.header) {
        this.setHeader({
          appName,
          imageSource: defaultHeaderImage,
          style: defaultHeaderStyle,
          imgStyle: defaultHeaderImageStyle
        });
      }

      if (!this.footer) {
        this.setFooter({})
      }
      
      return `
        <div style='${getStyleString({ ...this.bodyStyle, overflow: 'hidden' })}'>
          ${this.header}
          ${this.subheader}
          <div class='content' style='${getStyleString(this.contentStyle)}'>
            ${this.body}
          </div>

          ${this.footer}
        </div>
      `
    }

    get() {
      this.setSource(defaultEmailSource);
      const html = this.getHTML();
      this.setKeyTo('Message.Body.Html.Data', html);
      return this.data;
    }

    async send() {
      const template = this.get();
      try {
        const result = await fetch(endpoint, {
          method: 'post',
          body: JSON.stringify({
            data: template
          })
        })
        return result;
      } catch (e) {
        throw new Error(e);
      }
    }
  }

  return SESEmailBuilder;
};

module.exports = SESEmailBuilderFactory;
